import { ConfirmModal } from '@sparklayer-io/spark-dash-ui/components/confirm-modal';
import { Page } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { SectionForm, sectionFormCreateDefaultValues } from '../components/section-form';
import { useDeleteSection, useSection } from '../hooks/use-sections';
import { sectionToFormValues } from '../section-transformers';

export const SectionFormPage = withPageProviders(SectionFormPageContent);

function SectionFormPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const { id: companyId, sectionId } = navigationHandler.params();
  const isCreate = sectionId === undefined;

  const sectionsPath = `/customers/companies/${companyId}/sections`;

  const { data: section, isLoading, isError, refetch } = useSection(companyId, sectionId);
  const deleteSection = useDeleteSection(companyId, sectionId);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleDelete = () => {
    if (!sectionId) return;
    deleteSection.mutate(undefined, {
      onError: () => {
        notificationHandler.notify(t('notifications:titles.error'), t('errors:generic'), 'error');
      },
      onSuccess: () => {
        setDeleteModalOpen(false);
        notificationHandler.notify(
          t('notifications:titles.success'),
          t('notifications:deleted'),
          'success',
        );
        navigationHandler.navigate(sectionsPath);
      },
    });
  };

  const pageTitle = isCreate
    ? t('features:sections.pages.section_form_page.create_title')
    : t('features:sections.pages.section_form_page.edit_title', { name: section?.name ?? '' });

  const secondaryActions =
    !isCreate && section
      ? [
          {
            onClick: () => setDeleteModalOpen(true),
            label: t('buttons:delete'),
            icon: 'delete' as const,
            danger: true,
          },
        ]
      : undefined;

  return (
    <Page
      narrow
      title={pageTitle}
      backUrl={sectionsPath}
      secondaryActions={secondaryActions}
      description={t('features:sections.pages.section_form_page.description')}
    >
      <SparkSuspense
        isLoading={isLoading && !isCreate}
        isError={isError && !isCreate}
        errorMessage={t('errors:generic')}
        onReload={() => void refetch()}
      >
        <SectionForm
          defaultValues={
            isCreate || !section ? sectionFormCreateDefaultValues : sectionToFormValues(section)
          }
          isCreate={isCreate ? { parent: null, sibling: null } : undefined}
          companyId={companyId ?? ''}
          sectionId={sectionId}
          existingSection={section}
          onCancel={() => navigationHandler.navigate(sectionsPath)}
        />

        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDelete}
          loading={deleteSection.isPending}
          title={t('features:sections.pages.section_form_page.delete_confirm_title')}
          confirmText={t('buttons:delete')}
          message={t('features:sections.pages.section_form_page.delete_confirm_body', {
            name: section?.name ?? '',
          })}
        />
      </SparkSuspense>
    </Page>
  );
}
